import { Copy, ExternalLink, Play, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { webhooksApi } from '../lib/api-client';
import type { Flow } from '../types/api';

interface WebhookTestModalProps {
	flow: Flow;
	isOpen: boolean;
	onClose: () => void;
}

interface WebhookConfig {
	path?: string;
	authentication?: 'none' | 'signature' | 'token';
	secretKey?: string;
	headers?: Record<string, string>;
	expectedPayload?: {
		type: 'object';
		properties?: Record<string, any>;
		required?: string[];
	};
	responseConfig?: {
		successStatusCode?: number;
		successMessage?: string;
		errorStatusCode?: number;
		timeout?: number;
	};
}

/**
 * Modal component for testing webhook triggers
 */
export function WebhookTestModal({ flow, isOpen, onClose }: WebhookTestModalProps) {
	const [activeTab, setActiveTab] = useState<'payload' | 'headers' | 'history'>('payload');
	const [payload, setPayload] = useState('{}');
	const [headers, setHeaders] = useState<Record<string, string>>({});
	const [isLoading, setIsLoading] = useState(false);
	const [testResult, setTestResult] = useState<{
		success: boolean;
		executionId?: string;
		message: string;
	} | null>(null);

	// Extract webhook configuration from flow
	const webhookConfig: WebhookConfig | null = (() => {
		if (!flow.dagDefinition?.nodes) return null;

		const webhookNode = flow.dagDefinition.nodes.find(
			(node: any) => node.type === 'start.webhook'
		);

		// Try both possible locations for the config
		return webhookNode?.data?.config || webhookNode?.config || null;
	})();
	// Generate webhook URL (matching the pattern used in StartWebhookConfig)
	const webhookUrl = webhookConfig?.path
		? `${window.location.origin}/api/v1/triggers/webhook/${webhookConfig.path}`
		: 'No webhook path configured';

	// Generate sample payload based on webhook config
	useEffect(() => {
		if (webhookConfig?.expectedPayload?.properties) {
			const samplePayload: Record<string, any> = {};

			Object.entries(webhookConfig.expectedPayload.properties).forEach(
				([key, schema]: [string, any]) => {
					switch (schema.type) {
						case 'string':
							samplePayload[key] = schema.example || `sample_${key}`;
							break;
						case 'number':
							samplePayload[key] = schema.example || 123;
							break;
						case 'boolean':
							samplePayload[key] = schema.example || true;
							break;
						case 'object':
							samplePayload[key] = schema.example || {};
							break;
						case 'array':
							samplePayload[key] = schema.example || [];
							break;
						default:
							samplePayload[key] = schema.example || `sample_${key}`;
					}
				}
			);

			setPayload(JSON.stringify(samplePayload, null, 2));
		}
	}, [webhookConfig]);

	// Copy webhook URL to clipboard
	const copyToClipboard = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
			// Could add a toast notification here
		} catch (err) {
			console.error('Failed to copy to clipboard:', err);
		}
	};

	// Handle webhook test
	const handleTest = async () => {
		setIsLoading(true);
		setTestResult(null);

		try {
			// Parse payload JSON
			let parsedPayload;
			try {
				parsedPayload = JSON.parse(payload);
			} catch (err) {
				setTestResult({
					success: false,
					message: 'Invalid JSON in payload',
				});
				setIsLoading(false);
				return;
			}

			// Get webhook path from config
			const webhookPath = webhookConfig?.path;
			if (!webhookPath) {
				setTestResult({
					success: false,
					message: 'No webhook path configured in this workflow',
				});
				setIsLoading(false);
				return;
			}

			// Call the actual webhook endpoint
			const result = await webhooksApi.testWebhook(webhookPath, parsedPayload, headers);

			setTestResult({
				success: true,
				executionId: result.executionId,
				message: `Webhook test successful! Execution started with ID: ${result.executionId}`,
			});
		} catch (error: any) {
			// Handle different types of errors
			if (error.response?.status === 404) {
				setTestResult({
					success: false,
					message:
						'No published workflow found with this webhook path. Make sure the workflow is published.',
				});
			} else if (error.response?.status === 400) {
				setTestResult({
					success: false,
					message: error.response?.data?.message || 'Invalid webhook request or payload',
				});
			} else {
				setTestResult({
					success: false,
					message: error instanceof Error ? error.message : 'Test failed',
				});
			}
		} finally {
			setIsLoading(false);
		}
	};

	// Add header
	const addHeader = () => {
		setHeaders(prev => ({ ...prev, '': '' }));
	};

	// Update header
	const updateHeader = (oldKey: string, newKey: string, value: string) => {
		setHeaders(prev => {
			const newHeaders = { ...prev };
			if (oldKey !== newKey && oldKey in newHeaders) {
				delete newHeaders[oldKey];
			}
			if (newKey) {
				newHeaders[newKey] = value;
			}
			return newHeaders;
		});
	};

	// Remove header
	const removeHeader = (key: string) => {
		setHeaders(prev => {
			const newHeaders = { ...prev };
			delete newHeaders[key];
			return newHeaders;
		});
	};

	if (!isOpen) return null;

	return (
		<div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
			<div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl mx-4 w-full max-h-[90vh] flex flex-col">
				{/* Header */}
				<div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
					<div>
						<h2 className="text-xl font-semibold text-gray-900 dark:text-white">
							Test Webhook: {flow.name}
						</h2>
						<p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
							{flow.status === 'published'
								? 'Published workflow'
								: 'Draft workflow (test mode)'}
						</p>
					</div>
					<button
						onClick={onClose}
						className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
					>
						<X className="w-5 h-5" />
					</button>
				</div>

				{/* Content */}
				<div className="flex overflow-hidden flex-col flex-1">
					{/* Webhook URL Section */}
					<div className="p-6 bg-gray-50 border-b border-gray-200 dark:bg-gray-900 dark:border-gray-700">
						<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
							Webhook URL
						</label>
						<div className="flex gap-2">
							<input
								type="text"
								value={webhookUrl}
								readOnly
								className="flex-1 px-3 py-2 text-sm text-gray-900 bg-gray-100 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
							/>
							<button
								onClick={() => copyToClipboard(webhookUrl)}
								className="px-3 py-2 text-gray-700 bg-gray-200 rounded-md transition-colors dark:bg-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
								title="Copy to clipboard"
							>
								<Copy className="w-4 h-4" />
							</button>
							<a
								href={webhookUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="px-3 py-2 text-gray-700 bg-gray-200 rounded-md transition-colors dark:bg-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
								title="Open in new tab"
							>
								<ExternalLink className="w-4 h-4" />
							</a>
						</div>
					</div>

					{/* Tabs */}
					<div className="border-b border-gray-200 dark:border-gray-700">
						<nav className="flex px-6 space-x-8">
							{[
								{ id: 'payload' as const, label: 'Payload' },
								{ id: 'headers' as const, label: 'Headers' },
								{ id: 'history' as const, label: 'Test History' },
							].map(tab => (
								<button
									key={tab.id}
									onClick={() => setActiveTab(tab.id)}
									className={`py-4 px-1 border-b-2 font-medium text-sm ${
										activeTab === tab.id
											? 'border-blue-500 text-blue-600 dark:text-blue-400'
											: 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
									}`}
								>
									{tab.label}
								</button>
							))}
						</nav>
					</div>

					{/* Tab Content */}
					<div className="overflow-auto flex-1 p-6">
						{activeTab === 'payload' && (
							<div className="space-y-4">
								<div>
									<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
										JSON Payload
									</label>
									<textarea
										value={payload}
										onChange={e => setPayload(e.target.value)}
										className="px-3 py-2 w-full h-64 font-mono text-sm text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
										placeholder="Enter JSON payload..."
									/>
								</div>
								<button
									onClick={() =>
										setPayload(JSON.stringify(JSON.parse(payload), null, 2))
									}
									className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
									disabled={!payload.trim()}
								>
									Format JSON
								</button>
							</div>
						)}

						{activeTab === 'headers' && (
							<div className="space-y-4">
								<div className="flex justify-between items-center">
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										HTTP Headers
									</label>
									<button
										onClick={addHeader}
										className="px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
									>
										Add Header
									</button>
								</div>
								<div className="space-y-2">
									{Object.entries(headers).map(([key, value]) => (
										<div key={key} className="flex gap-2">
											<input
												type="text"
												value={key}
												onChange={e =>
													updateHeader(key, e.target.value, value)
												}
												placeholder="Header name"
												className="flex-1 px-3 py-2 text-sm text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
											/>
											<input
												type="text"
												value={value}
												onChange={e =>
													updateHeader(key, key, e.target.value)
												}
												placeholder="Header value"
												className="flex-1 px-3 py-2 text-sm text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
											/>
											<button
												onClick={() => removeHeader(key)}
												className="px-3 py-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
											>
												<X className="w-4 h-4" />
											</button>
										</div>
									))}
									{Object.keys(headers).length === 0 && (
										<p className="text-sm text-gray-500 dark:text-gray-400">
											No custom headers configured. Click "Add Header" to add
											one.
										</p>
									)}
								</div>
							</div>
						)}

						{activeTab === 'history' && (
							<div className="space-y-4">
								<h3 className="text-lg font-medium text-gray-900 dark:text-white">
									Recent Tests
								</h3>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									Test history will appear here after running webhook tests.
								</p>
							</div>
						)}
					</div>

					{/* Test Result */}
					{testResult && (
						<div
							className={`mx-6 mb-4 p-4 rounded-md ${
								testResult.success
									? 'bg-green-50 border border-green-200 dark:bg-green-900 dark:border-green-700'
									: 'bg-red-50 border border-red-200 dark:bg-red-900 dark:border-red-700'
							}`}
						>
							<div
								className={`text-sm font-medium ${
									testResult.success
										? 'text-green-800 dark:text-green-300'
										: 'text-red-800 dark:text-red-300'
								}`}
							>
								{testResult.success ? 'Test Successful' : 'Test Failed'}
							</div>
							<div
								className={`text-sm mt-1 ${
									testResult.success
										? 'text-green-700 dark:text-green-400'
										: 'text-red-700 dark:text-red-400'
								}`}
							>
								{testResult.message}
							</div>
							{testResult.executionId && (
								<div className="mt-2 text-sm">
									<span className="font-medium">Execution ID:</span>{' '}
									{testResult.executionId}
								</div>
							)}
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700">
					<div className="text-sm text-gray-500 dark:text-gray-400">
						{webhookConfig?.authentication && webhookConfig.authentication !== 'none'
							? `Authentication: ${webhookConfig.authentication}`
							: 'No authentication required'}
					</div>
					<div className="flex gap-3">
						<button
							onClick={onClose}
							className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md transition-colors dark:text-gray-300 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500"
						>
							Close
						</button>
						<button
							onClick={handleTest}
							disabled={isLoading || !payload.trim()}
							className="flex gap-2 items-center px-4 py-2 text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<Play className="w-4 h-4" />
							{isLoading ? 'Testing...' : 'Send Test'}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
